import { create } from 'zustand'
import { apiFetch, formatApiError } from '@/lib/api'
import { ACTIVE_ORG_KEY } from '@/lib/constants'
import { slugify } from '@/lib/utils'
import type { Org } from '@/types/api'

type OrgState = {
  orgs: Org[]
  activeOrgId: string | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  switchOrg: (id: string) => void
  createOrg: (name: string) => Promise<Org>
}

function readStored(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY)
  } catch {
    return null
  }
}

export const useOrg = create<OrgState>((set, get) => ({
  orgs: [],
  activeOrgId: readStored(),
  loading: true,
  error: null,

  refresh: async () => {
    set({ loading: true, error: null })
    try {
      const { orgs } = await apiFetch<{ orgs: Org[] }>('/orgs')
      const list = orgs ?? []
      const stored = get().activeOrgId
      const valid = stored && list.some((o) => o.id === stored) ? stored : (list[0]?.id ?? null)
      if (valid !== stored) {
        try {
          if (valid) localStorage.setItem(ACTIVE_ORG_KEY, valid)
          else localStorage.removeItem(ACTIVE_ORG_KEY)
        } catch {}
      }
      set({ orgs: list, activeOrgId: valid, loading: false })
    } catch (e) {
      set({ error: formatApiError(e, 'Failed to load orgs'), loading: false })
    }
  },

  switchOrg: (id) => {
    try {
      localStorage.setItem(ACTIVE_ORG_KEY, id)
    } catch {}
    set({ activeOrgId: id })
  },

  createOrg: async (name) => {
    const body = { name, slug: slugify(name) }
    const { org } = await apiFetch<{ org: Org }>('/orgs', {
      method: 'POST',
      body: JSON.stringify(body),
    })
    if (!org?.id) throw new Error('Create org: unexpected response')
    const { orgs } = get()
    const next = [...orgs, org]
    try {
      localStorage.setItem(ACTIVE_ORG_KEY, org.id)
    } catch {}
    set({ orgs: next, activeOrgId: org.id })
    return org
  },
}))
